import { useEffect, useState } from "react";
import { AlertCircle } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { findHoliday, holidayName } from "@/lib/holidays-sg";

/**
 * High-contrast alert that ONLY renders when today is a public holiday
 * at the Mels site. Designed to be impossible to miss — sits above the
 * small inline OfficeStatus pill in the contact section.
 */
const COPY = {
  de: {
    label: "Heute geschlossen",
    middot: "Feiertag:",
    hint: "Wir sind am nächsten Werktag wieder persönlich für Sie erreichbar.",
  },
  fr: {
    label: "Fermé aujourd'hui",
    middot: "Jour férié :",
    hint: "Nous sommes à nouveau joignables personnellement le prochain jour ouvré.",
  },
  it: {
    label: "Oggi chiuso",
    middot: "Festività:",
    hint: "Saremo nuovamente raggiungibili di persona il prossimo giorno lavorativo.",
  },
  en: {
    label: "Closed today",
    middot: "Public holiday:",
    hint: "We'll be reachable in person again on the next business day.",
  },
};

export default function TodayClosedBanner({ className = "" }) {
  const { lang } = useI18n();
  const c = COPY[lang] || COPY.de;
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    // Re-check around midnight so the banner appears/disappears at the
    // correct moment without a page reload.
    const t = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(t);
  }, []);

  const holiday = findHoliday(now);
  if (!holiday) return null;

  return (
    <div
      data-testid="today-closed-banner"
      role="alert"
      className={`relative overflow-hidden border border-amber-400/40 bg-amber-400/[0.08] ${className}`}
    >
      {/* subtle diagonal warning stripe at the very top */}
      <div className="absolute inset-x-0 top-0 h-[3px] bg-gradient-to-r from-amber-400 via-amber-300 to-amber-500" />
      <div className="flex items-start gap-4 p-5 md:p-6 pt-6">
        <div className="flex-none mt-0.5">
          <span className="relative flex items-center justify-center w-9 h-9 rounded-full bg-amber-400/15 ring-1 ring-amber-400/40">
            <AlertCircle className="w-5 h-5 text-amber-300" strokeWidth={1.75} />
            <span className="absolute inset-0 rounded-full bg-amber-400/30 animate-ping" />
          </span>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80 mb-1">
            {c.middot}
          </div>
          <div className="font-display text-white text-xl md:text-2xl tracking-tight leading-tight">
            {c.label}
            <span className="text-amber-300"> — {holidayName(holiday.key, lang)}</span>
          </div>
          <p className="text-zinc-300 text-sm mt-2 leading-relaxed max-w-md">
            {c.hint}
          </p>
        </div>
      </div>
    </div>
  );
}
